<div class="cl-table">
  <feedback-card [message]="errorMsg" feedBackType="error" />
  <feedback-card [message]="successMsg" feedBackType="success" />

  <div class="bk-panel cl-table__panel">
    <!-- Phones hide the header row, so its select-all moves here. -->
    @if (rows.length > 0) {
      <label class="cl-selectall">
        <input
          type="checkbox"
          class="cl-check"
          [checked]="allContactsSelected"
          [indeterminate]="someOnPageSelected"
          (change)="toggleCheckAll()"
        />
        Select all on this page
      </label>
    }
    <data-table label="Contacts" [seamless]="true" [loading]="loading" [maxHeight]="'640px'" [skeletonColumns]="7">
      @if (rows.length > 0 || loading) {
        <table class="bk-log">
          <thead>
            <tr>
              <th scope="col" class="col-check">
                <input
                  type="checkbox"
                  class="cl-check"
                  [checked]="allContactsSelected"
                  [indeterminate]="someOnPageSelected"
                  (change)="toggleCheckAll()"
                  aria-label="Select every contact on this page"
                />
              </th>
              <th scope="col" class="col-name">Contact</th>
              <th scope="col" class="col-phone">Phone</th>
              <th scope="col" class="col-orgs">Organizations</th>
              <th scope="col" class="col-tags">Tags</th>
              <th scope="col" class="col-source">Source</th>
              <th scope="col" class="col-added">Added</th>
              <th scope="col" class="col-actions"><span class="bk-sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            @for (row of rows; track row.key) {
              <tr
                [class.is-link]="canViewDetails"
                [class.is-selected]="checkedMember(row.contact)"
                [attr.tabindex]="canViewDetails ? 0 : null"
                (click)="openRow(row)"
                (keydown.enter)="openFromKeyboard($event, row)"
              >
                <td class="col-check" (click)="$event.stopPropagation()">
                  <input
                    type="checkbox"
                    class="cl-check"
                    [checked]="checkedMember(row.contact)"
                    (change)="select(row.contact)"
                    [attr.aria-label]="'Select ' + row.name"
                  />
                </td>
                <td class="col-name">
                  <span class="bk-msg">
                    <span class="bk-msg__subject">{{ row.name }}</span>
                    <span class="bk-msg__preview">
                      @if (row.email) {
                        {{ row.email }}
                      } @else {
                        <span class="cl-faint">No email</span>
                      }
                    </span>
                  </span>
                </td>
                <td class="col-phone">
                  <span class="cl-phone">
                    <span class="cl-phone__number">{{ row.phone }}</span>
                    @if (row.phoneIssue) {
                      <app-status class="cl-phone__issue" [text]="row.phoneIssue" status="failed" />
                    }
                  </span>
                </td>
                <!-- "Acme +2" on screen; the full list is in the title and read out in full. -->
                <td class="quiet col-orgs" [attr.title]="row.organizationsTitle">
                  @if (row.organizationsTitle) {
                    <span aria-hidden="true">{{ row.organizations }}</span>
                    <span class="bk-sr-only">{{ row.organizationsTitle }}</span>
                  } @else {
                    {{ row.organizations }}
                  }
                </td>
                <td class="quiet col-tags" [attr.title]="row.tagsTitle">
                  @if (row.tagsTitle) {
                    <span aria-hidden="true">{{ row.tags }}</span>
                    <span class="bk-sr-only">{{ row.tagsTitle }}</span>
                  } @else {
                    {{ row.tags }}
                  }
                </td>
                <td class="quiet col-source">{{ row.source }}</td>
                <td class="quiet col-added" [attr.title]="row.addedTitle">{{ row.added }}</td>
                <td class="col-actions">
                  <span class="bk-row-actions">
                    <button
                      *hasPermission="{ category: 'CONTACT_MANAGEMENT', permission: 'edit-contacts' }"
                      type="button"
                      class="bk-icon-btn"
                      [attr.aria-label]="'Edit ' + row.name"
                      matTooltip="Edit"
                      (click)="$event.stopPropagation(); openEditContact(row.contact)"
                    >
                      <span class="bk-sym" aria-hidden="true">edit</span>
                    </button>
                    <button
                      *hasPermission="{ category: 'CONTACT_MANAGEMENT', permission: 'delete-contacts' }"
                      type="button"
                      class="bk-icon-btn cl-danger"
                      [attr.aria-label]="'Delete ' + row.name"
                      matTooltip="Delete"
                      (click)="$event.stopPropagation(); openDeletePrompt(row.contact)"
                    >
                      <span class="bk-sym" aria-hidden="true">delete</span>
                    </button>
                    @if (canViewDetails) {
                      <span class="bk-sym row-go" aria-hidden="true">chevron_right</span>
                    }
                  </span>
                </td>
              </tr>
            }
          </tbody>
        </table>
      } @else {
        <app-empty-state
          icon="contacts"
          message="No contacts on this page"
          [query]="searchTerm"
          [filtered]="filtered"
          size="medium"
          (clear)="clearFilters.emit()"
        />
      }
    </data-table>
  </div>

  <div class="cl-table__pager">
    <app-pagination
      (changePage)="updateContactsPage($event)"
      [currentPage]="contactsPage"
      [totalPages]="totalContactsPages"
      [totalItems]="totalContacts"
      [pageSize]="pageSize"
      (onNext)="goToNextPage()"
      (onPrev)="goToPreviousPage()"
      [loading]="contactService.isLoading || contactService.isFetching"
      (pageSizeChange)="onPageSizeChange($event)"
    />
  </div>

  <!-- MODALS -->
  @if (openEditContactModal) {
    <edit-contact-modal [selectedContact]="selectedContact" (closeModalEvent)="closeEditContact()" />
  }

  @if (showContactDetail) {
    <contact-detail (closeModal)="closeContactDetail()" [contact]="selectedContact" />
  }

  <app-prompt-card
    buttonText="Delete contact"
    title="Delete this contact?"
    tone="danger"
    impact="This cannot be undone."
    impactIcon="warning"
    [openModal]="showDeletePrompt"
    (onClose)="closeDeletePrompt()"
    (onButtonClick)="deleteContact()"
    [loading]="contactLoading"
  >
    <p class="cl-prompt">
      <strong>{{ selectedName }}</strong> will be removed from your contacts, organizations and tags.
    </p>
  </app-prompt-card>
</div>
